/**
 * shell-chat-defaults — per-shell default open state for the chat drawer.
 *
 * IA v3 (Phase 5) UX contract:
 *   - Dashboard (including former commerce routes): chat drawer is CLOSED
 *     on first visit; user opens it with the chat toggle in DashboardHeader.
 *   - IDE: chat drawer stays OPEN by default (existing `chatSidebarOpen:true`
 *     default in `store/ui-slice.ts` — unchanged).
 *
 * Design decision — "apply on shell CHANGE, not shell MOUNT":
 *   - `chatSidebarOpen` remains a single store boolean (no new state slice)
 *     so the toggle button on every shell keeps working without special cases.
 *   - A module-level `lastAppliedShell` remembers which shell was active the
 *     last time a default was applied. Each shell calls
 *     `useChatDefaultForShell(shell)` on mount; the hook rewrites
 *     `chatSidebarOpen` IFF the currently-mounting shell differs from the
 *     last one that applied. Re-mounting the same shell does NOT fight
 *     the user's explicit toggle because `shell` is unchanged.
 *   - Without this, a Dashboard (closed) → IDE transition would leak
 *     `false` into IDE and the user would perceive it as a regression
 *     ("why is my chat hidden when I opened a project?").
 *
 * Phase 5 note: the `'commerce'` literal was removed from `StudioShell`
 * because the Commerce shell itself was reverted (commerce is now a
 * Dashboard sub-area). Callers pass `'dashboard'` regardless of route.
 */
import { useEffect } from 'react'
import { useStudioStore } from '@/store/studio-store'
import {
  SHELL_CHAT_DRAWER_DEFAULT_OPEN as DEFAULT_OPEN,
  type StudioShell,
} from './shell-chat-drawer-defaults'

export type { StudioShell }

// Module-level — resets on full page reload. Tracks the shell whose default
// was last committed to the store, so shell transitions re-apply but intra-
// shell remounts do not fight the user's explicit toggle.
//
// KNOWN, and why the project-exit path does not rely on this hook: only ONE
// caller exists (`studio-dashboard-shell.tsx:310`, `'dashboard'`). Nothing
// ever applies `'ide'`, so `lastAppliedShell` reaches `'dashboard'` on the
// first dashboard mount and every later dashboard mount early-returns —
// while every project-open transaction writes `chatSidebarOpen = true`.
// Returning to the dashboard therefore left the drawer open, which is what
// hid the hero composer in slice-5A §A5-a. `store/project-session-exit.ts`
// applies the dashboard default at the exit seam, where leaving a project is
// a fact rather than a mount ordering.
let lastAppliedShell: StudioShell | null = null

export function useChatDefaultForShell(shell: StudioShell) {
  useEffect(() => {
    if (lastAppliedShell === shell) return
    lastAppliedShell = shell
    useStudioStore.getState().setChatSidebarOpen(DEFAULT_OPEN[shell])
  }, [shell])
}

/** Test helper — reset the module state so per-test isolation is possible. */
export function _resetChatDefaultsApplied() {
  lastAppliedShell = null
}
